import React, { useCallback, useMemo, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { useTheme } from '@chakra-ui/react';
import type { Theme } from '@emotion/react';
import { Elements } from '@stripe/react-stripe-js';
import { type StripeElementsOptions, loadStripe } from '@stripe/stripe-js';
import ResponsiveButtonGroup from 'ts/common/components/ResponsiveButtonGroup';
import type { ButtonProps } from 'ts/common/components/gallery';
import { useTranslate } from 'ts/common/hooks';
import { useClientBookingExperience } from 'client_react/booking/ClientBookingExperience';
import BookingSessionHeading from 'client_react/booking/components/BookingSessionHeading';
import PaymentForm from 'client_react/booking/components/PaymentForm';
import PaymentOptions from 'client_react/booking/components/PaymentOptions';
import useBookingFlowSteps from 'client_react/booking/hooks/useBookingFlowSteps';
import { useBootstrapData } from 'client_react/bootstrap';
import type { ClientPaymentOption } from 'client_react/common/components/ClientPaymentOptions';
import { getStripeAppearance, getStripeFonts } from 'client_react/common/helpers';

const PaymentPage = () => {
    const { bookingSession, bookingSessionType, isPending, submitPayment } =
        useClientBookingExperience();

    const { goToPreviousStep, goToNextStep } = useBookingFlowSteps();

    const [paymentAmountType, setPaymentAmountType] = useState<ClientPaymentOption>('full');

    const t = useTranslate('booking.payment');
    const theme = useTheme() as unknown as Theme;
    const { staticUrl } = useBootstrapData();

    const handleSubmitPayLater = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault();
            await submitPayment({ paymentAmountType: 'retainer' });
            goToNextStep();
        },
        [goToNextStep, submitPayment]
    );

    const payLaterButtons = useMemo<Array<ButtonProps>>(
        () => [
            {
                text: t('back'),
                variant: 'outline primary',
                onClick: () => goToPreviousStep()
            },
            {
                text: t('bookSession'),
                isDisabled: isPending,
                variant: 'primary',
                type: 'submit'
            }
        ],
        [goToPreviousStep, isPending, t]
    );

    const stripeInstance = useMemo(() => {
        if (!bookingSessionType) {
            return null;
        }

        return loadStripe(bookingSessionType.stripePublicKey, {
            apiVersion: '2023-10-16',
            stripeAccount: bookingSessionType.stripeStandardAccountId ?? undefined
        });
    }, [bookingSessionType]);

    const stripeOptions = useMemo(() => {
        if (!bookingSession) {
            return undefined;
        }

        const { stripePrice, stripeRetainerAmount } = bookingSession;

        const options: StripeElementsOptions = {
            amount: paymentAmountType === 'full' ? stripePrice : stripeRetainerAmount,
            appearance: getStripeAppearance(theme),
            currency: bookingSession.currencyCode.toLowerCase(),
            fonts: getStripeFonts(staticUrl),
            mode: 'payment',
            paymentMethodCreation: 'manual'
        };

        return options;
    }, [bookingSession, paymentAmountType, staticUrl, theme]);

    if (!bookingSession || !bookingSessionType) {
        return null;
    }

    const isPayingNow = paymentAmountType === 'full' || bookingSession.stripeRetainerAmount > 0;

    return (
        <>
            <BookingSessionHeading
                bookingSession={bookingSession}
                bookingSessionType={bookingSessionType}
            />
            {isPayingNow ? (
                <Elements options={stripeOptions} stripe={stripeInstance}>
                    <PaymentForm
                        paymentAmountType={paymentAmountType}
                        setPaymentAmountType={setPaymentAmountType}
                    />
                </Elements>
            ) : (
                <Flex as="form" flexDirection="column" gap="24px" onSubmit={handleSubmitPayLater}>
                    <PaymentOptions
                        paymentAmountType={paymentAmountType}
                        setPaymentAmountType={setPaymentAmountType}
                    />
                    <ResponsiveButtonGroup buttons={payLaterButtons} marginTop="32px" />
                </Flex>
            )}
        </>
    );
};

export default PaymentPage;
